import { z } from "zod";
import {
  emailValidation,
  firstnameValidation,
  lastnameValidation,
} from "./invitations";

/**
 * Client Validation Schemas
 *
 * Aligned with OpenAPI spec v2.3.69 — POST /api/v1/client (ClientCreate schema)
 */

export const STANDARD_MARKET_VALUES = [
  "Canadian",
  "European",
  "United States",
] as const;

export const STANDARD_MARKETS = [
  { value: "", label: "Select a market" },
  { value: "Canadian", label: "Canadian" },
  { value: "European", label: "European" },
  { value: "United States", label: "United States" },
] as const;

export const BILLING_TIER_VALUES = ["trial", "free", "paid"] as const;

const dateStringSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v), {
    message: "Date must be YYYY-MM-DD",
  });

/**
 * Client create form validation schema.
 * Only `name` is required per the API contract. Address and config fields are optional.
 */
export const clientCreateSchema = z
  .object({
    name: z
      .string()
      .min(2, "Organization name must be at least 2 characters")
      .max(70, "Organization name must be under 70 characters")
      .trim(),
    street_address: z
      .string()
      .max(70, "Street address must be under 70 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    city: z
      .string()
      .max(70, "City must be under 70 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    state: z
      .string()
      .max(70, "State/Province must be under 70 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    zip_code: z
      .string()
      .max(7, "ZIP/Postal code must be at most 7 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    country_region: z
      .string()
      .max(70, "Country/Region must be under 70 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    standard_market: z
      .enum(STANDARD_MARKET_VALUES)
      .nullable()
      .optional()
      .or(z.literal("")),
    mix_optimization_platform: z
      .string()
      .max(70, "Mix optimization platform must be under 70 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    in_transit_system: z
      .string()
      .max(70, "In-transit system must be under 70 characters")
      .trim()
      .optional()
      .or(z.literal("")),
    client_support_user_id: z
      .string()
      .uuid("Invalid user ID")
      .optional()
      .or(z.literal("")),
    logo: z
      .string()
      .max(2_000_000, "Logo must be under 2 MB")
      .nullable()
      .optional()
      .or(z.literal("")),
    billing_tier: z.enum(BILLING_TIER_VALUES).optional(),
    initialization_date: dateStringSchema.optional().or(z.literal("")),
    send_admin_invite: z.boolean().optional(),
    admin_firstname: z
      .union([z.literal(""), firstnameValidation.trim()])
      .optional(),
    admin_lastname: z
      .union([z.literal(""), lastnameValidation.trim()])
      .optional(),
    admin_email: z.union([z.literal(""), emailValidation.trim()]).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.billing_tier === "trial" && !data.initialization_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Trial end date is required when billing tier is trial",
        path: ["initialization_date"],
      });
    }
    if (data.send_admin_invite) {
      if (!data.admin_firstname || data.admin_firstname.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First name is required when sending an invite",
          path: ["admin_firstname"],
        });
      }
      if (!data.admin_lastname || data.admin_lastname.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last name is required when sending an invite",
          path: ["admin_lastname"],
        });
      }
      if (!data.admin_email || data.admin_email.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Email is required when sending an invite",
          path: ["admin_email"],
        });
      }
    }
  });

/**
 * Type for client create form
 */
export type ClientCreateForm = z.infer<typeof clientCreateSchema>;

/**
 * Client update validation schema (form-side, for edit modal)
 * All fields optional per PATCH semantics.
 */
export const clientUpdateSchema = z.object({
  name: z
    .string()
    .min(1, "Organization name must be at least 1 character")
    .max(70, "Organization name must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  street_address: z
    .string()
    .max(70, "Street address must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  city: z
    .string()
    .max(70, "City must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  state: z
    .string()
    .max(70, "State/Province must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  zip_code: z
    .string()
    .max(7, "ZIP/Postal code must be at most 7 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  country_region: z
    .string()
    .max(70, "Country/Region must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  standard_market: z
    .enum(STANDARD_MARKET_VALUES)
    .nullable()
    .optional()
    .or(z.literal("")),
  mix_optimization_platform: z
    .string()
    .max(70, "Mix optimization platform must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  in_transit_system: z
    .string()
    .max(70, "In-transit system must be under 70 characters")
    .trim()
    .optional()
    .or(z.literal("")),
  logo: z
    .string()
    .max(2_000_000, "Logo must be under 2 MB")
    .nullable()
    .optional()
    .or(z.literal("")),
});

/**
 * Type for client update form
 */
export type ClientUpdateForm = z.infer<typeof clientUpdateSchema>;

// =============================================================================
// SERVER-SIDE API VALIDATION SCHEMAS
// =============================================================================

/**
 * Server-side client create validation schema.
 * Matches the backend API contract (ClientCreate) — only name is required.
 */
export const clientCreateApiSchema = z
  .object({
    name: z
      .string()
      .min(2, "Organization name must be at least 2 characters")
      .max(70, "Organization name must be under 70 characters")
      .trim(),
    street_address: z.string().max(70).trim().optional(),
    city: z.string().max(70).trim().optional(),
    state: z.string().max(70).trim().optional(),
    zip_code: z.string().max(7).trim().optional(),
    country_region: z.string().max(70).trim().optional(),
    standard_market: z.enum(STANDARD_MARKET_VALUES).nullable().optional(),
    mix_optimization_platform: z.string().max(70).trim().optional(),
    in_transit_system: z.string().max(70).trim().optional(),
    client_support_user_id: z.string().uuid().optional(),
    logo: z.string().max(2_000_000).nullable().optional(),
    billing_tier: z.enum(BILLING_TIER_VALUES).optional(),
    initialization_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.billing_tier === "trial" && !data.initialization_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Trial end date is required when billing tier is trial",
        path: ["initialization_date"],
      });
    }
  });

/**
 * Server-side client update validation schema (PATCH — all optional).
 */
export const clientUpdateApiSchema = z.object({
  name: z.string().max(70).trim().optional(),
  street_address: z.string().max(70).trim().optional(),
  city: z.string().max(70).trim().optional(),
  state: z.string().max(70).trim().optional(),
  zip_code: z.string().max(7).trim().optional(),
  country_region: z.string().max(70).trim().optional(),
  standard_market: z.enum(STANDARD_MARKET_VALUES).nullable().optional(),
  mix_optimization_platform: z.string().max(70).trim().optional(),
  in_transit_system: z.string().max(70).trim().optional(),
  client_support_user_id: z.string().uuid().optional(),
  logo: z.string().max(2_000_000).nullable().optional(),
});
